import React, { useEffect, useState, useCallback } from "react";
import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { aiHealthAPI } from "../../lib/companyAdminApi";
import {
  RefreshCw, Activity, CheckCircle, AlertCircle, Clock,
  Zap, Star, Server, Wifi, WifiOff, ChevronLeft, ChevronRight, Calendar,
  AlertTriangle, TrendingUp,
} from "lucide-react";

// ─── helpers ────────────────────────────────────────────────────────────────

function toLocalYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function addDays(ymd, delta) {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return toLocalYMD(d);
}
function formatDisplayDate(ymd) {
  const d = new Date(ymd + "T00:00:00");
  const today = toLocalYMD(new Date());
  const yesterday = addDays(today, -1);
  if (ymd === today)     return "Today";
  if (ymd === yesterday) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

// ─── constants ───────────────────────────────────────────────────────────────

const PROMPT_LABELS = {
  chat:           { label: "Chat",            color: "bg-blue-500" },
  nutrition:      { label: "Nutrition",        color: "bg-green-500" },
  image:          { label: "Image AI",         color: "bg-purple-500" },
  moderate_relax: { label: "Moderate & Relax", color: "bg-amber-500" },
};

const PROVIDER_COLORS = {
  Google:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200"    },
  OpenAI:    { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  Anthropic: { bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200"  },
  Mistral:   { bg: "bg-violet-50",  text: "text-violet-700",  border: "border-violet-200"  },
  DeepSeek:  { bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200"    },
  Groq:      { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200"    },
  Gemma:     { bg: "bg-teal-50",    text: "text-teal-700",    border: "border-teal-200"    },
};

// ─── sub-components ──────────────────────────────────────────────────────────

function HealthBadge({ status }) {
  if (status === "active") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" /> Active
    </span>
  );
  if (status === "healthy") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
      <CheckCircle className="w-3 h-3" /> Healthy
    </span>
  );
  if (status === "failing") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-200">
      <AlertTriangle className="w-3 h-3" /> Failing
    </span>
  );
  if (status === "degraded") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
      <AlertCircle className="w-3 h-3" /> Degraded
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-muted-foreground border border-slate-200">
      <Clock className="w-3 h-3" /> Idle
    </span>
  );
}

function ConnBadge({ status }) {
  return status === "healthy"
    ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-200"><Wifi className="w-3 h-3" /> Online</span>
    : <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-200"><WifiOff className="w-3 h-3" /> Offline</span>;
}

function PromptRow({ label, colorClass, success, failed }) {
  const total = success + failed;
  const pct = total > 0 ? Math.round((success / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex items-center gap-2 w-40 shrink-0">
        <span className={"w-2 h-2 rounded-full shrink-0 " + colorClass} />
        <span className="text-xs text-slate-600 font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-3 flex-1">
        <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
          {total > 0
            ? <div className="h-full rounded-full bg-green-500 transition-all duration-500" style={{ width: pct + "%" }} />
            : <div className="h-full rounded-full bg-slate-200 w-full" />}
        </div>
        <div className="flex items-center gap-3 shrink-0 text-xs">
          <span className="flex items-center gap-1 text-green-600 font-semibold w-12"><CheckCircle className="w-3 h-3" />{success}</span>
          <span className="flex items-center gap-1 text-red-500 font-medium w-12"><AlertCircle className="w-3 h-3" />{failed}</span>
          <span className="text-slate-400 w-10 text-right">{total > 0 ? pct + "%" : "—"}</span>
        </div>
      </div>
    </div>
  );
}

function ModelCard({ model }) {
  const pc = PROVIDER_COLORS[model.provider] || { bg: "bg-muted", text: "text-slate-700", border: "border-slate-200" };
  const successRate = model.total_calls > 0 ? Math.round((model.total_success / model.total_calls) * 100) : null;
  return (
    <div className="bg-card border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className={"px-5 py-4 border-b " + pc.border + " " + pc.bg}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={"text-xs font-bold px-2 py-0.5 rounded-md border " + pc.text + " bg-card/70 " + pc.border}>{model.provider}</span>
              {model.is_default && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">
                  <Star className="w-2.5 h-2.5" /> Default
                </span>
              )}
              <span className={"text-[10px] font-medium px-2 py-0.5 rounded-full border " + (model.tier === "free" ? "bg-green-50 text-green-600 border-green-200" : "bg-muted text-slate-600 border-slate-200")}>
                {model.tier === "free" ? "Free" : "Paid"}
              </span>
            </div>
            <h3 className={"mt-1.5 text-sm font-bold truncate " + pc.text}>{model.name}</h3>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">{model.model_id}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <HealthBadge status={model.health_status} />
            {successRate !== null && <span className="text-xs text-muted-foreground">{successRate}% success</span>}
          </div>
        </div>
        {model.total_calls > 0 ? (
          <div className="mt-3 flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1 text-green-600 font-semibold"><Zap className="w-3 h-3" />{model.total_success.toLocaleString()} ok</span>
            <span className="text-red-400 font-medium">{model.total_failed.toLocaleString()} failed</span>
            <span className="text-slate-400 ml-auto">{model.total_calls} total calls</span>
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-400 italic">No calls recorded on this date</p>
        )}
      </div>
      <div className="px-5 divide-y divide-slate-50">
        <div className="flex items-center gap-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          <span className="w-40 shrink-0">Prompt</span>
          <span className="flex-1">Rate</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-green-500 w-12">Success</span>
            <span className="text-red-400 w-12">Failed</span>
            <span className="w-10 text-right">%</span>
          </div>
        </div>
        {Object.entries(PROMPT_LABELS).map(([key, { label, color }]) => (
          <PromptRow key={key} label={label} colorClass={color}
            success={model.prompts[key]?.success || 0}
            failed={model.prompts[key]?.failed || 0} />
        ))}
      </div>
    </div>
  );
}

function GemmaCard({ server }) {
  const online = server.connectivity === "healthy";
  return (
    <div className="bg-card border border-teal-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="px-5 py-4 border-b border-teal-200 bg-teal-50">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold px-2 py-0.5 rounded-md border text-teal-700 bg-card/70 border-teal-200 flex items-center gap-1">
                <Server className="w-3 h-3" /> Gemma 4 (Self-hosted)
              </span>
              {server.is_default && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">
                  <Star className="w-2.5 h-2.5" /> Default
                </span>
              )}
            </div>
            <h3 className="mt-1.5 text-sm font-bold truncate text-teal-700">{server.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">{server.api_url}</p>
          </div>
          <ConnBadge status={server.connectivity} />
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          {online
            ? <span className="flex items-center gap-1 text-green-600 font-semibold"><CheckCircle className="w-3 h-3" /> Server reachable</span>
            : <span className="flex items-center gap-1 text-red-500 font-medium"><AlertCircle className="w-3 h-3" /> Server unreachable</span>}
          <span className="text-slate-400 ml-auto text-[10px]">model: gemma4:e4b</span>
        </div>
      </div>
      <div className="px-5 py-4">
        <p className="text-xs text-muted-foreground">
          Ollama self-hosted inference at <span className="font-mono text-teal-600">{server.api_url}/v1</span>.
          Served via queue proxy (1 request at a time).
        </p>
      </div>
    </div>
  );
}

// ─── Date Navigator ──────────────────────────────────────────────────────────

function DateNavigator({ date, onChange }) {
  const today = toLocalYMD(new Date());
  const isToday = date === today;
  return (
    <div className="flex items-center gap-2 bg-card border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm">
      <button
        onClick={() => onChange(addDays(date, -1))}
        className="p-1 rounded-lg hover:bg-slate-100 text-muted-foreground transition-colors"
        title="Previous day"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <div className="relative flex items-center gap-2 min-w-[160px] justify-center">
        <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-sm font-semibold text-slate-700">{formatDisplayDate(date)}</span>
        {/* hidden native date input layered on top for click */}
        <input
          type="date"
          value={date}
          max={today}
          onChange={e => e.target.value && onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer w-full"
          title="Pick a date"
        />
      </div>

      <button
        onClick={() => !isToday && onChange(addDays(date, 1))}
        disabled={isToday}
        className="p-1 rounded-lg hover:bg-slate-100 text-muted-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        title="Next day"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {!isToday && (
        <button
          onClick={() => onChange(today)}
          className="ml-1 text-xs font-semibold text-primary hover:underline px-1"
        >
          Today
        </button>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function AIHealthPage() {
  const [date, setDate]       = useState(() => toLocalYMD(new Date()));
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await aiHealthAPI.getHealth(d);
      setData(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error("AI Health fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(date); }, [date, fetchData]);

  const handleDateChange = (d) => { setDate(d); };

  const free   = data?.models?.filter(m => m.tier === "free")  || [];
  const paid   = data?.models?.filter(m => m.tier !== "free")  || [];
  const gemma  = data?.gemma_servers || [];
  const totalCalls = data?.models?.reduce((s, m) => s + (m.total_calls || 0), 0) || 0;
  const totalOk    = data?.models?.reduce((s, m) => s + (m.total_success || 0), 0) || 0;
  const gemmaOnline = gemma.filter(g => g.connectivity === "healthy").length;

  // Default model status for warning banner
  const defaultModel = data?.models?.find(m => m.is_default);
  const defaultFailing = defaultModel && defaultModel.health_status === "failing";
  const fallbackModel = defaultFailing
    ? data?.models?.find(m => !m.is_default && m.total_success > 0)
    : null;

  return (
    <CompanyAdminLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Activity className="w-6 h-6 text-primary" /> AI Health Monitor
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Prompt call stats for the selected date · Gemma server live connectivity
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <DateNavigator date={date} onChange={handleDateChange} />
            <div className="flex items-center gap-2">
              {lastRefresh && (
                <span className="text-xs text-muted-foreground hidden sm:block">
                  {lastRefresh.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => fetchData(date)}
                disabled={loading}
                className="p-2 rounded-lg border border-border hover:bg-muted text-muted-foreground transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={"w-4 h-4 " + (loading ? "animate-spin" : "")} />
              </button>
            </div>
          </div>
        </div>

        {/* Default model failing warning */}
        {!loading && defaultFailing && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">
                Default model ({defaultModel?.name}) is failing today (rate limit / quota exceeded)
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                {fallbackModel
                  ? `Requests are being served by ${fallbackModel.name} via automatic failover.`
                  : "All models attempted in failover order. Check API key quotas in AI Config."}
                {" "}Fix: update your Gemini API key quota at{" "}
                <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer"
                  className="underline font-medium">aistudio.google.com</a>.
              </p>
            </div>
          </div>
        )}

        {/* Summary bar */}
        {!loading && data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-slate-800">{data.total_enabled || 0}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Enabled Models</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{totalCalls}</p>
              <p className="text-xs text-blue-600 mt-0.5">Total Calls Today</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{totalOk}</p>
              <p className="text-xs text-green-600 mt-0.5">Successful</p>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-teal-700">{gemmaOnline}/{gemma.length}</p>
              <p className="text-xs text-teal-600 mt-0.5">Gemma Online</p>
            </div>
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading stats for {formatDisplayDate(date)}...
          </div>
        ) : !data || (data.total_enabled === 0 && gemma.length === 0) ? (
          <div className="text-center py-24 text-muted-foreground">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-semibold">No enabled AI models</p>
            <p className="text-xs mt-1">Enable AI models or add Gemma 4 servers in AI Config.</p>
          </div>
        ) : (
          <>
            {/* Gemma servers */}
            {gemma.length > 0 && (
              <section>
                <SectionHeader icon={<Server className="w-3.5 h-3.5" />} label="Gemma 4 Self-Hosted" count={gemma.length} colorClass="bg-teal-100 text-teal-600 border-teal-200" />
                <div className="grid gap-4 xl:grid-cols-2 mt-3">
                  {gemma.map(s => <GemmaCard key={s.id} server={s} />)}
                </div>
              </section>
            )}

            {/* Free models */}
            {free.length > 0 && (
              <section>
                <SectionHeader icon={null} label="Free Models" count={free.length} colorClass="bg-green-100 text-green-600 border-green-200" />
                <div className="grid gap-4 xl:grid-cols-2 mt-3">
                  {free.map(m => <ModelCard key={m.id} model={m} />)}
                </div>
              </section>
            )}

            {/* Paid models */}
            {paid.length > 0 && (
              <section>
                <SectionHeader icon={null} label="Paid Models" count={paid.length} colorClass="bg-slate-100 text-slate-600 border-slate-200" />
                <div className="grid gap-4 xl:grid-cols-2 mt-3">
                  {paid.map(m => <ModelCard key={m.id} model={m} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </CompanyAdminLayout>
  );
}

function SectionHeader({ icon, label, count, colorClass }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</span>
      <span className={"text-xs font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 " + colorClass}>
        {icon}{count}
      </span>
    </div>
  );
}
